// TableFilterProvider.js
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOperators } from 'dashboard/components-next/filter/operators.js';
import { useStore } from 'vuex';

/**
 * @typedef {Object} FilterOption
 * @property {string|number} id
 * @property {string} name
 * @property {import('vue').VNode} [icon]
 */

/**
 * @typedef {Object} FilterOperator
 * @property {string} value
 * @property {string} label
 * @property {string} icon
 * @property {boolean} hasInput
 */

/**
 * @typedef {Object} FilterType
 * @property {string} attributeKey - The attribute key (column name)
 * @property {string} value - Proxy for the attribute key used in FilterSelect
 * @property {string} attributeName - The attribute name used to display on the UI
 * @property {string} label - Proxy for the attribute name used in FilterSelect
 * @property {'multiSelect'|'searchSelect'|'plainText'|'number'|'date'|'booleanSelect'} inputType - The input type for the attribute
 * @property {FilterOption[]} [options] - Options available for the attribute if it is a multiSelect or singleSelect type
 * @property {'text'|'number'} dataType - The data type of the column
 * @property {FilterOperator[]} filterOperators - The operators available for the attribute
 * @property {'standard'|'additional'|'customAttributes'} attributeModel
 */

/**
 * @typedef {Object} FilterGroup
 * @property {string} name
 * @property {FilterType[]} attributes
 */

/**
 * Composable that provides table column filtering context
 * @returns {{ filterTypes: import('vue').ComputedRef<FilterType[]>, setSelectedTable: Function, tableColumns: import('vue').Ref }}
 */
export function useTableFilterContext() {
  const { t } = useI18n();
  const store = useStore();

  const { equalityOperators, containmentOperators, getOperatorTypes } =
    useOperators();

  // Estado reativo para a tabela selecionada e suas colunas
  const selectedTable = ref(null);
  const tableColumns = ref([]);

  // Função para atualizar a tabela selecionada e buscar colunas
  const setSelectedTable = async tableName => {
    selectedTable.value = tableName;
    if (tableName) {
      try {
        await store.dispatch('externalDatabaseConnection/describeTable', {
          tableName,
        });
        tableColumns.value =
          store.getters['externalDatabaseConnection/getTableDescription'](
            tableName
          ) || [];
      } catch (error) {
        console.error(`Erro ao descrever tabela ${tableName}:`, error);
        tableColumns.value = [];
      }
    } else {
      tableColumns.value = [];
    }
  };

  /**
   * @type {import('vue').ComputedRef<FilterType[]>}
   */
  const filterTypes = computed(() => {
    if (!tableColumns.value || tableColumns.value.length === 0) {
      return [];
    }

    return tableColumns.value.map(column => {
      const columnName = column.column_name;
      const dataType = column.data_type.toLowerCase();

      // Determina o tipo de entrada e operadores com base no data_type
      let inputType;
      let operators;
      let mappedDataType;

      if (dataType.includes('date') || dataType.includes('timestamp')) {
        inputType = 'date';
        // Lista própria de coluna de banco externo: inclui >= e <= (o core
        // não cobre esses em data, então dateOperators fica de fora daqui)
        operators = getOperatorTypes('dbDateColumn');
        mappedDataType = 'text';
      } else if (
        dataType.includes('int') ||
        dataType.includes('numeric') ||
        // O data_type vem CRU do information_schema do banco do PROVEDOR:
        // MySQL (IXC e afins) reporta coluna de dinheiro como `decimal` (no
        // Postgres decimal vira `numeric`) e float4 do Postgres é `real`.
        // Sem estes aliases a coluna cai no ramo de texto e perde >, >=, <, <=.
        dataType.includes('decimal') ||
        dataType.includes('real') ||
        dataType.includes('float') ||
        dataType.includes('double')
      ) {
        inputType = 'number';
        operators = getOperatorTypes('dbNumericColumn');
        mappedDataType = 'number';
      } else if (dataType.includes('bool')) {
        inputType = 'booleanSelect';
        operators = equalityOperators.value;
        mappedDataType = 'text';
        return {
          attributeKey: columnName,
          value: columnName,
          attributeName: columnName,
          label: columnName,
          inputType,
          options: [
            { id: 'true', name: t('FILTER.BOOLEAN.TRUE') },
            { id: 'false', name: t('FILTER.BOOLEAN.FALSE') },
          ],
          dataType: mappedDataType,
          filterOperators: operators,
          attributeModel: 'standard',
        };
      } else {
        inputType = 'plainText';
        operators = containmentOperators.value;
        mappedDataType = 'text';
      }

      return {
        attributeKey: columnName,
        value: columnName,
        attributeName: columnName,
        label: columnName,
        inputType,
        dataType: mappedDataType,
        filterOperators: operators,
        attributeModel: 'standard',
      };
    });
  });

  // Exportando tableColumns junto com filterTypes e setSelectedTable
  return { filterTypes, setSelectedTable, tableColumns };
}
